/**
 * @description HeatmapDragService module.
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module services/HeatmapDragService
 */

const rowHeight = 32;

/**
 * @description Service to handle heatmap dragging, expanding and scrolling.
 * @class
 */
class HeatmapDragService {

    /**
     * Creates an instance of HeatmapDragService.
     * @param {Object} contentScrollbar - The main content scrollbar.
     */
    constructor(contentScrollbar) {
        this.contentScrollbar = contentScrollbar;
        this.dragTimerActive = false;
        this.scrollSpacerHeight = 0;

        this.domElements = {
            content: document.querySelector('.content'),
            scrollSpacer: document.querySelector('.scroll-space'),
            footer: document.querySelector('.Footer'),
            currentDragHandle: null
        };

        this.setDragEvents();
        this.handleScroll = this.recalculateScrollSpacer.bind(this);

        this.minimumHeatmapHeight = 50 + (rowHeight * 8);
        this.minimumHeatmapHeightLive = this.minimumHeatmapHeight;
    }

    /**
     * @description Updates local references to DOM elements.
     * @returns {void}
     */
    updateScrollbarDomElements() {
        if (this.domElements.scrollbar) {
            return;
        }

        this.domElements = {
            ...this.domElements,
            scrollbar: this.contentScrollbar.current.getScrollElement(),
            scrollbarContentWrapper: this.contentScrollbar.current.contentWrapperEl,
            scrollbarContent: this.contentScrollbar.current.contentEl
        };
    }

    /**
     * @description Sets the drag events handler objects. 
     * These contain handlers for specific events.
     */
    setDragEvents() {
        this.dragEvents = [
            {
                element: this.domElements.content,
                events: [
                    {
                        eventName: 'mousemove',
                        handler: this.handleHeatmapDrag.bind(this)
                    }
                ]
            },
            {
                element: this.domElements.footer,
                events: [
                    {
                        eventName: 'mouseover',
                        handler: this.handleDragOverFooter.bind(this)
                    },
                    {
                        eventName: 'mouseout',
                        handler: this.handleDragOutFooter.bind(this)
                    }
                ]
            },
            {
                element: document,
                events: [
                    {
                        eventName: 'mouseup',
                        handler: this.handleEndDragging.bind(this)
                    }
                ]
            }
        ];
    }

    /**
     * @description Adds the event listeners as specified in the this.dragEvents object.
     */
    addDragEventListeners() {
        this.dragEvents.forEach(element =>
            element.events.forEach(eventListener =>
                element.element.addEventListener(eventListener.eventName, eventListener.handler)
            )
        );
    }

    /**
     * @description Removes the event listeners in the this.dragEvents object.
     */
    removeDragEventListeners() {
        this.dragEvents.forEach(element =>
            element.events.forEach(eventListener =>
                element.element.removeEventListener(eventListener.eventName, eventListener.handler)
            )
        );
    }

    /**
     * @description Sets the initial variables required when dragging starts.
     * @param {Object} event - The drag event.
     * @param {number} initialHeight - The initial height of the heatmap.
     * @param {number} numberOfRows - The number of rows displayed in the heatmap.
     * @param {Function} updateHeightCallback - The callback in which to return the calculated height.
     * @param {Function} endDragCallback - The callback to call at the end of the drag.
     */
    handleStartDragging(event, initialHeight, numberOfRows, updateHeightCallback, endDragCallback, isLive) {
        this.updateScrollbarDomElements();
        this.minimumHeatmapHeight = isLive ? this.minimumHeatmapHeightLive : this.minimumHeatmapHeight;
        this.isExpanding = true;
        this.initialY = event.clientY;
        this.initialSpacerHeight = this.scrollSpacerHeight;
        this.domElements.currentDragHandle = event.currentTarget;
        this.initialHeight = initialHeight;
        this.currentHeatmapHeight = initialHeight;
        this.numberOfRows = numberOfRows;
        this.updateHeightCallback = updateHeightCallback;
        this.endDragCallback = endDragCallback;
        this.addDragEventListeners();
    }

    /**
     * @description Handles the end of the drag.
     */
    handleEndDragging() {
        this.removeDragEventListeners();
        clearInterval(this.overFooterInterval);

        if (this.scrollSpacerHeight !== 'auto') {
            this.scrollbarScrollTop = this.domElements.scrollbar.scrollTop;
            this.onScroll = this.recalculateScrollSpacer.bind(this);
            this.domElements.scrollbar.addEventListener('scroll', this.handleScroll);
        }

        this.endDragCallback && this.endDragCallback(this.currentHeatmapHeight !== this.minimumHeatmapHeight);
        this.isExpanding = false;
    }

    /**
     * @description Handles the drag move events.
     * @param {Object} event - The move event.
     * @returns {void}
     */
    handleHeatmapDrag(event) {
        const isAboveHandle = this.domElements.currentDragHandle.getBoundingClientRect().top >= event.clientY;

        if (
            !this.isExpanding ||
            this.dragTimerActive ||
            (this.currentHeatmapHeight === this.minimumHeatmapHeight && isAboveHandle)
        ) {
            return;
        }

        const scrolledToBottom =
            this.domElements.scrollbar.clientHeight + this.domElements.scrollbar.scrollTop >=
            this.domElements.scrollbarContent.offsetHeight;

        if (isAboveHandle && scrolledToBottom) {
            this.updateScrollSpacer(this.initialSpacerHeight + (this.initialY - event.clientY));
        } else if (this.scrollSpacerHeight) {
            this.updateScrollSpacer(this.initialSpacerHeight - (event.clientY - this.initialY));
        }

        // Timer used for debouncing - increases performance of drag.
        this.dragTimerActive = true;
        setTimeout(() => (this.dragTimerActive = false), 10);

        const newHeight = event.clientY - this.initialY + this.initialHeight,
            maxHeightByRows = this.numberOfRows * 32 + this.minimumHeatmapHeight,
            maxHeightByWindow = this.domElements.content.clientHeight - 64,
            maxHeight = Math.min(maxHeightByRows, maxHeightByWindow),
            calculatedHeight = Math.min(Math.max(newHeight, this.minimumHeatmapHeight), maxHeight);

        this.currentHeatmapHeight = calculatedHeight;
        this.updateHeightCallback && this.updateHeightCallback(this.currentHeatmapHeight);
    }

    /**
     * @description Handles the case where a drag handle is pulled over the footer.
     */
    handleDragOverFooter() {
        const stepHeight = 20,
            maxHeightByRows = this.numberOfRows * 32 + this.minimumHeatmapHeight,
            maxHeightByWindow = this.domElements.content.clientHeight - 64,
            maxHeight = Math.min(maxHeightByRows, maxHeightByWindow);

        // Interval used for continuation fo scroll when over footer.
        this.overFooterInterval = setInterval(() => {
            const currentScroll = this.domElements.scrollbarContentWrapper.scrollTop,
                newHeight = Math.min(Math.max(this.currentHeatmapHeight + stepHeight, this.minimumHeatmapHeight), maxHeight);

            if (newHeight === this.currentHeatmapHeight) {
                return;
            }
            
            this.currentHeatmapHeight = newHeight;
            this.startHeight += stepHeight;
            this.updateHeightCallback(newHeight, () => {
                this.domElements.scrollbarContentWrapper.scrollTop = currentScroll + stepHeight;
            });
        }, 100);
    }

    /**
     * @description Handles dragging out of the footer.
     * @param {Object} event - The mouseout event.
     */
    handleDragOutFooter(event) {
        this.initialY = event.clientY;
        this.initialHeight = this.currentHeatmapHeight
        clearInterval(this.overFooterInterval);
    }

    /**
     * @description Updates the scroll spacer height.
     * @param {number} [newHeight=0] - The new height of the spacer.
     */
    updateScrollSpacer(newHeight = 0) {
        if (newHeight < 0) {
            newHeight = 0;
        }

        this.scrollSpacerHeight = newHeight;
        this.domElements.scrollSpacer.style.height = newHeight > 0 ? `${newHeight}px` : 'auto';
    }

    /**
     * @description Recalculates the scroll spacer height.
     * @returns {void}
     */
    recalculateScrollSpacer() {
        const heightAdjustment = this.scrollbarScrollTop - this.domElements.scrollbar.scrollTop,
            newHeight = this.scrollSpacerHeight - heightAdjustment;

        this.scrollbarScrollTop = this.domElements.scrollbar.scrollTop;

        if (newHeight <= 0) {
            // scrollSpacer now effectively 0
            this.domElements.scrollbar.removeEventListener('scroll', this.handleScroll);
            this.updateScrollSpacer();

            return;
        } else if (heightAdjustment <= 0) {
            // Scrolling down - don't change scrollSpacer height.
            return;
        }

        this.updateScrollSpacer(newHeight);
    }
}

export default HeatmapDragService;
